import { useNavigate } from "react-router-dom";
import { ImageOff, CreditCard, ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * FeaturedProductDisplay — full-width spotlight banner for a single product.
 * Used in the "Select Products" sections of the storefront grid.
 *
 * Props:
 *   product     — product object from API
 *   reverse     — bool; flips image/text column order for visual variety
 *   onBuyNow    — optional buy-now handler override
 *   accent      — "primary" (default) | "dark"  — background theme
 */
export default function FeaturedProductDisplay({ product, reverse = false, onBuyNow, accent = "primary" }) {
  const navigate = useNavigate();
  if (!product) return null;

  const id = product.mongo_id || product.id;

  // Use first frame of photo_urls array when available; fall back to img_url
  const displayImg = product.photo_urls?.[0] || product.img_url || null;

  const hasDiscount =
    product.original_price && product.sale_price &&
    product.sale_price < product.original_price;

  const discountPct = hasDiscount
    ? Math.round(((product.original_price - product.sale_price) / product.original_price) * 100)
    : null;

  const isDark = accent === "dark";

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        borderRadius: "var(--radius-card)",
        background: isDark
          ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.06))"
          : "linear-gradient(135deg, rgba(91,140,255,0.06), rgba(168,85,247,0.04))",
        border: isDark
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(91,140,255,0.15)",
      }}
    >
      <div
        className={`flex flex-col md:flex-row ${reverse ? "md:flex-row-reverse" : ""} items-stretch min-h-[320px]`}
      >
        {/* ── Image panel ── */}
        <div
          className="relative flex items-center justify-center overflow-hidden flex-shrink-0"
          style={{
            width: "100%",
            maxWidth: 420,
            minHeight: 260,
            background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.04)",
          }}
        >
          {displayImg ? (
            <img
              src={displayImg}
              alt={product.product_name}
              className="w-full h-full object-cover"
              style={{ minHeight: 260, maxHeight: 380 }}
            />
          ) : (
            <div
              className="flex flex-col items-center gap-3 w-full h-full"
              style={{ minHeight: 260, justifyContent: "center" }}
            >
              <div
                className="flex items-center justify-center"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "calc(var(--radius) * 2)",
                  background: isDark ? "rgba(255,255,255,0.04)" : "rgba(91,140,255,0.06)",
                }}
              >
                <ImageOff className="h-10 w-10" style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.3)" }} />
              </div>
              <span
                className="font-body text-[10px] uppercase tracking-widest"
                style={{ color: isDark ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.4)" }}
              >
                Images Coming Soon
              </span>
            </div>
          )}

          {/* Discount badge overlay */}
          {discountPct && (
            <div
              className="absolute top-4 left-4 font-heading font-black text-xs px-3 py-1"
              style={{ color: "var(--vb-blue)", background: "rgba(91,140,255,0.15)", borderRadius: "var(--radius)" }}
            >
              -{discountPct}% OFF
            </div>
          )}
        </div>

        {/* ── Content panel ── */}
        <div
          className="flex flex-col justify-center px-8 py-8 flex-1"
          style={{ gap: "1rem" }}
        >
          {/* Category + brand row */}
          <div className="flex items-center gap-2 flex-wrap">
            {product.machine_type && (
              <span
                className="font-body text-[10px] uppercase tracking-wider px-2.5 py-1"
                style={{
                  borderRadius: "calc(var(--radius) * 0.75)",
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(91,140,255,0.1)",
                  color: isDark ? "#5B8CFF" : "#5B8CFF",
                  border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(91,140,255,0.2)",
                }}
              >
                {product.machine_type}
              </span>
            )}
            {product.brand_name && (
              <span
                className="font-body text-[10px] uppercase tracking-wider"
                style={{ color: isDark ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.45)" }}
              >
                {product.brand_name}
              </span>
            )}
          </div>

          {/* Headline */}
          <div>
            {product.headline && (
              <p
                className="font-body text-sm mb-1"
                style={{ color: isDark ? "#A855F7" : "#5B8CFF" }}
              >
                {product.headline}
              </p>
            )}
            <h2
              className="font-heading font-black leading-tight"
              style={{
                fontSize: "clamp(1.3rem, 3vw, 1.75rem)",
                color: isDark ? "hsl(0 0% 95%)" : "hsl(0 0% 95%)",
              }}
            >
              {product.product_name}
            </h2>
            {product.tagline && (
              <p
                className="font-body text-sm mt-1.5 leading-relaxed"
                style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.5)" }}
              >
                {product.tagline}
              </p>
            )}
          </div>

          {/* Bullet points */}
          {product.great_points?.length > 0 && (
            <ul className="flex flex-col gap-2">
              {product.great_points.slice(0, 3).map((pt, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2
                    className="h-3.5 w-3.5 flex-shrink-0 mt-0.5"
                    style={{ color: "var(--vb-blue)" }}
                  />
                  <span
                    className="font-body text-xs leading-relaxed"
                    style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.55)" }}
                  >
                    {pt}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Pricing + CTAs */}
          <div className="flex flex-col gap-3 mt-1">
            {/* Price */}
            <div className="flex items-baseline gap-3 flex-wrap">
              {product.sale_price ? (
                <>
                  <span
                    className="font-heading font-black text-2xl"
                    style={{ color: "var(--vb-blue)" }}
                  >
                    ${product.sale_price?.toLocaleString()}
                  </span>
                  {hasDiscount && (
                    <>
                      <span
                        className="font-body text-sm line-through"
                        style={{ color: isDark ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.4)" }}
                      >
                        ${product.original_price?.toLocaleString()}
                      </span>
                      <span
                        className="px-2 py-0.5 font-heading font-bold text-[10px] uppercase tracking-wider"
                        style={{ background: "rgba(91,140,255,0.12)", color: "var(--vb-blue)", borderRadius: "var(--radius)" }}
                      >
                        You Save ${(product.original_price - product.sale_price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <span
                  className="font-body text-sm italic"
                  style={{ color: isDark ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.4)" }}
                >
                  Custom pricing — request a quote
                </span>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-2.5 flex-wrap">
              {product.sale_price && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onBuyNow ? onBuyNow(product) : navigate(`/product/${id}`);
                  }}
                  className="flex items-center justify-center gap-1.5 font-heading font-bold text-xs uppercase tracking-wider text-white transition-all duration-150 hover:opacity-90 active:scale-95 px-5 py-2.5 text-center"
                  style={{
                    background: "hsl(var(--primary))",
                    borderRadius: "var(--radius-btn)",
                  }}
                >
                  {`Secure This Machine | ${product.sale_price?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/product/${id}`);
                }}
                className="flex items-center gap-1.5 font-heading font-semibold text-xs uppercase tracking-wider transition-all duration-150 hover:opacity-80 active:scale-95 px-5 py-2.5"
                style={{
                  borderRadius: "var(--radius)",
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(255,255,255,0.15)",
                  color: isDark ? "#5B8CFF" : "#D9D9D9",
                  background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.06)",
                }}
              >
                View Details
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
